/**
 * Web chat tool-card for application tracking (`applicationTrack`,
 * `catalogApplyTrack`, `applicationListByJob`).
 *
 * Every application is one task row: the job as the label, the company as the
 * amount, a status badge and pill derived from the latest status-history entry,
 * and the history itself behind the chevron. Web-only; `index.native.tsx`
 * renders the iOS/Android twin through the same `task-rows-model`.
 */
import React, { useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { CardShell } from './card-shell';
import { CARD_PADDING_X } from './card-ui';
import type { ToolCardProps } from './types';
import { asArray } from './types';
import { TaskRows } from '../task-rows';
import { applicationsToTaskRows, type ApplicationLike } from '../task-rows-model';

const MAX_DISPLAY = 5;

export const ApplicationListCard: FC<ToolCardProps> = ({ result }) => {
  const { t } = useTranslation('common');
  const applications = asArray<ApplicationLike>(result);
  const rows = useMemo(
    () => applicationsToTaskRows(applications.slice(0, MAX_DISPLAY), t),
    [applications, t],
  );
  if (applications.length === 0) return null;

  const remaining = applications.length - MAX_DISPLAY;

  return (
    <CardShell>
      <TaskRows rows={rows} variant="list" />
      {remaining > 0 ? (
        <div
          style={{
            fontSize: 12,
            color: 'var(--text-tertiary)',
            padding: `6px ${CARD_PADDING_X}px 12px`,
          }}
        >
          {t('chat.tasks.more', { count: remaining })}
        </div>
      ) : null}
    </CardShell>
  );
};
